<template>
    <CustomDialog
        :value="value"
        @input="$emit('input', $event)"
        :defaultTitle="modalTitle"
        :heightClass="`ppv2-max-h-[85vh]`"
        height="65%"
        centerContent
    >
        <template #content>
            <div class="ppv2-grid ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                <!-- Name -->
                <BaseInput
                    v-model="categoryForm.name"
                    :label="nameLabel"
                    :placeholder="namePlaceholder"
                    required
                    classContainerAdd="ppv2-w-full"
                    :error="errors.name ? errors.name[0] : null"
                />

                <!-- Parent Category -->
                <BaseSelect
                    v-model="categoryForm.parent_id"
                    :label="$t('food.categories.modal.parent_label')"
                    :placeholder="$t('food.categories.modal.parent_placeholder')"
                    :options="parentCategoryOptions"
                    optionLabel="name"
                    optionValue="id"
                    classAdded="borderInput"
                    fixed-dropdown
                />
            </div>
            <div class="ppv2-grid ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                <!-- Slug -->
                <BaseInput
                    v-model="categoryForm.slug"
                    :label="$t('food.categories.modal.slug_label')"
                    :placeholder="$t('food.categories.modal.slug_placeholder')"
                    classContainerAdd="ppv2-w-full"
                    :error="errors.slug ? errors.slug[0] : null"
                />

                <!-- Tags -->
                <BaseSelect
                    v-model="categoryForm.tags"
                    :label="$t('food.categories.modal.tags_label')"
                    :placeholder="$t('food.categories.modal.tags_placeholder')"
                    :options="tagsOptions"
                    :reduce="(opt) => opt.label"
                    optionLabel="label"
                    multiple
                    taggable
                    classAdded="borderInput"
                    fixed-dropdown
                />
            </div>
            <!-- Active Status Switch -->
            <div class="ppv2-grid ppv2-grid-cols-2" :style="{ gap: normalizeSize(16, true) }">
                <div class="isNotrequired">
                    <BaseLabel :label="$t('food.categories.modal.status_label')" />
                    <div class="ppv2-mt-2">
                        <button
                            @click="categoryForm.active = !categoryForm.active"
                            class="ppv2-inline-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-2 ppv2-rounded-xl ppv2-border ppv2-transition-all"
                            :class="
                                categoryForm.active
                                    ? 'ppv2-border-[#206A64] ppv2-bg-[#06B217] ppv2-bg-opacity-20'
                                    : 'ppv2-border-[#4E4E4E] ppv2-bg-[#474747] ppv2-bg-opacity-20'
                            "
                        >
                            <div
                                class="ppv2-w-10 ppv2-h-5 ppv2-rounded-full ppv2-flex ppv2-items-center ppv2-transition-all"
                                :class="
                                    categoryForm.active
                                        ? 'ppv2-bg-[#206A64] ppv2-justify-end'
                                        : 'ppv2-bg-[#4E4E4E] ppv2-justify-start'
                                "
                            >
                                <div class="ppv2-w-4 ppv2-h-4 ppv2-bg-white ppv2-rounded-full ppv2-mx-0.5"></div>
                            </div>
                            <span class="poppinsRegular ppv2-text-gray-700">
                                {{
                                    categoryForm.active
                                        ? $t('food.categories.modal.active')
                                        : $t('food.categories.modal.inactive')
                                }}
                            </span>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Color Picker -->
            <div class="ppv2-mt-4 isNotrequired">
                <BaseLabel :label="$t('food.categories.modal.color_label')" />
                <div class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-my-2 ppv2-flex-wrap">
                    <!-- Predefined Colors -->
                    <button
                        v-for="color in computedAvailableColors"
                        :key="color"
                        @click="categoryForm.color = color"
                        class="ppv2-w-8 ppv2-h-8 ppv2-rounded-lg ppv2-transition-all ppv2-border ppv2-border-gray-200"
                        :class="{
                            'ppv2-ring-2 ppv2-ring-offset-2 ppv2-ring-purple-500':
                                categoryForm.color.toLowerCase() === color.toLowerCase(),
                        }"
                        :style="{ backgroundColor: color }"
                    ></button>

                    <!-- Custom Color Input with Picker -->
                    <div class="ppv2-relative">
                        <div
                            ref="colorInputTrigger"
                            class="ppv2-flex ppv2-items-center ppv2-border ppv2-border-gray-300 ppv2-rounded-lg ppv2-px-2 ppv2-h-8 ppv2-bg-white ppv2-cursor-pointer hover:ppv2-border-gray-400"
                            @click.stop="toggleColorPicker"
                        >
                            <span class="ppv2-text-gray-500 ppv2-mr-1">#</span>
                            <input
                                type="text"
                                :value="categoryForm.color.replace('#', '')"
                                class="ppv2-w-20 ppv2-outline-none ppv2-text-sm ppv2-uppercase ppv2-text-gray-700 ppv2-cursor-pointer"
                                readonly
                            />
                        </div>

                        <!-- Color Picker Popover -->
                        <div
                            v-if="showColorPicker"
                            class="ppv2-absolute ppv2-z-50 ppv2-right-0 ppv2-bottom-full ppv2-mb-2"
                            @click.stop
                        >
                            <div class="ppv2-fixed ppv2-inset-0 ppv2-z-40" @click="showColorPicker = false"></div>
                            <div class="ppv2-relative ppv2-z-50">
                                <sketch-picker :value="categoryForm.color" @input="updateColor" />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="ppv2-mt-4 ppv2-text-center">
                <CustomInputUpload v-model="categoryForm.image" :label="$t('food.illustration')" @input="onImageUpload" />
            </div>
        </template>
        <template #actions>
            <BaseButton
                variant="primary"
                @click="$emit('save')"
                :customClass="`!ppv2-h-[45px] !ppv2-min-w-[170px]`"
                :disabled="isSaving"
            >
                {{ isSaving ? $t('common.saving') : isEditMode ? $t('common.validate') : $t('common.add') }}
            </BaseButton>
        </template>
    </CustomDialog>
</template>

<script>
import normalizeSize from '../../../utils/normalizeSize'

import BaseButton from '../../Shared/BaseButton.vue'
import BaseInput from '../../Shared/BaseInput.vue'
import BaseSelect from '../../Shared/BaseSelect.vue'
import BaseLabel from '../../Shared/BaseLabel.vue'
import CustomDialog from '../../Shared/CustomDialog.vue'
import CustomInputUpload from '../../Inputs/CustomInputUpload.vue'
import { Sketch } from 'vue-color'

export default {
    name: 'CategoryModal',
    components: {
        BaseButton,
        BaseInput,
        BaseSelect,
        BaseLabel,
        CustomDialog,
        CustomInputUpload,
        'sketch-picker': Sketch,
    },
    props: {
        value: { type: Boolean, required: true },
        isEditMode: { type: Boolean, default: false },
        isSaving: { type: Boolean, default: false },
        categoryForm: { type: Object, required: true },
        parentCategories: { type: Array, default: () => [] },
        availableColors: { type: Array, default: () => [] },
        tagsOptions: { type: Array, default: () => [] },
        errors: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            showColorPicker: false,
            pickerPosition: 'bottom',
        }
    },
    computed: {
        modalTitle() {
            if (this.categoryForm.parent_id) {
                return this.isEditMode
                    ? this.$t('food.categories.modal.title_edit_sub')
                    : this.$t('food.categories.modal.title_create_sub')
            }
            return this.isEditMode ? this.$t('food.categories.modal.title_edit') : this.$t('food.categories.modal.title_create')
        },
        nameLabel() {
            return this.categoryForm.parent_id
                ? this.$t('food.categories.modal.name_label_sub')
                : this.$t('food.categories.modal.name_label')
        },
        namePlaceholder() {
            return this.categoryForm.parent_id
                ? this.$t('food.categories.modal.name_placeholder_sub')
                : this.$t('food.categories.modal.name_placeholder')
        },
        computedAvailableColors() {
            const colors = [...this.availableColors]
            const currentColor = this.categoryForm.color
            // Check if current color exists (case insensitive)
            const exists = colors.some((c) => c.toLowerCase() === currentColor.toLowerCase())

            if (currentColor && !exists) {
                colors.push(currentColor)
            }
            return colors
        },
        parentCategoryOptions() {
            return [{ id: null, name: this.$t('common.none') }, ...this.parentCategories]
        },
    },
    methods: {
        normalizeSize,
        toggleColorPicker() {
            if (!this.showColorPicker) {
                // Calculate position immediately before showing
                const trigger = this.$refs.colorInputTrigger
                if (trigger) {
                    const rect = trigger.getBoundingClientRect()
                    const spaceBelow = window.innerHeight - rect.bottom
                    // Lower threshold to favor bottom positioning since modal expands
                    this.pickerPosition = spaceBelow < 200 ? 'top' : 'bottom'
                }
                this.showColorPicker = true
            } else {
                this.showColorPicker = false
            }
        },
        updateColor(color) {
            this.categoryForm.color = color.hex
        },
        onImageUpload(file) {
            this.categoryForm.image = file
        },
    },
}
</script>
